import React from 'react';
import { Link } from 'react-router-dom';
import SearchBar from '../components/SearchBar';
import { stateAbbreviations } from '../services/listingHelpers';
import { useIsMobile } from '../hooks/useIsMobile';
import { trackAnalyticsEvent } from '../services/analytics';

const popularStates = ['FL', 'CA', 'TX', 'NY', 'OH', 'GA', 'PA', 'NC', 'AZ', 'CO'];
const stateHubPath = (abbr: string) =>
  `/rehabs/${stateAbbreviations[abbr].toLowerCase().replace(/\s+/g, '-')}`;

const HomePage: React.FC = () => {
  const isMobile = useIsMobile();
  return (
    <div>
      <section style={{
        background: 'linear-gradient(135deg, #1a237e 0%, #283593 50%, #0d47a1 100%)',
        color: '#fff', padding: isMobile ? '40px 16px' : '80px 20px', textAlign: 'center',
      }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <h1 style={{ fontSize: isMobile ? '32px' : '48px', fontWeight: 800, margin: '0 0 16px', lineHeight: 1.2 }}>
            Find the Right Rehab for You
          </h1>
          <p style={{ fontSize: isMobile ? '16px' : '20px', opacity: 0.9, margin: '0 0 32px', lineHeight: 1.5 }}>
            Browse thousands of addiction treatment centers, sober living homes, and recovery programs. Read real reviews and get help now.
          </p>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <SearchBar />
          </div>
          <p style={{ margin: '20px 0 0', fontSize: '14px', opacity: 0.7 }}>
            Free &amp; confidential — Available 24/7
          </p>
        </div>
      </section>

      <section style={{ padding: '60px 20px', background: '#f8f9fa' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{ textAlign: 'center', fontSize: '32px', marginBottom: '8px' }}>Browse by State</h2>
          <p style={{ textAlign: 'center', color: '#666', marginBottom: '40px' }}>
            Find accredited rehab centers near you
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '12px', marginBottom: '24px' }}>
            {popularStates.map(abbr => (
              <a key={abbr} href={stateHubPath(abbr)} style={{
                display: 'block', padding: '16px', background: '#fff',
                borderRadius: '8px', textAlign: 'center', textDecoration: 'none',
                color: '#1a237e', fontWeight: 600, fontSize: '15px',
                border: '1px solid #e0e0e0', transition: 'all 0.2s',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
              }}>
                {stateAbbreviations[abbr]}
              </a>
            ))}
          </div>
          <div style={{ textAlign: 'center' }}>
            <a href="/rehabs" style={{
              display: 'inline-block', padding: '12px 32px',
              border: '2px solid #1a237e', borderRadius: '8px',
              color: '#1a237e', fontWeight: 600, textDecoration: 'none',
            }}>View All States →</a>
          </div>
        </div>
      </section>

      <section style={{ padding: '60px 20px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{ textAlign: 'center', fontSize: '32px', marginBottom: '8px' }}>Types of Treatment</h2>
          <p style={{ textAlign: 'center', color: '#666', marginBottom: '40px' }}>
            We list facilities for every stage of recovery
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(240px, 100%), 1fr))', gap: '20px' }}>
            {[
              { title: 'Medical Detox', desc: 'Safe, medically supervised withdrawal from substances', icon: '🏥', type: 'Detox' },
              { title: 'Inpatient Rehab', desc: 'Residential treatment with 24/7 clinical support', icon: '🛏️', type: 'Inpatient' },
              { title: 'Outpatient Programs', desc: 'Flexible treatment while living at home', icon: '🏠', type: 'Outpatient' },
              { title: 'Sober Living', desc: 'Structured, substance-free housing for recovery', icon: '🏡', type: 'Sober Living' },
              { title: 'Partial Hospitalization', desc: 'Intensive day treatment programs (PHP)', icon: '🏗️', type: 'Partial Hospitalization' },
              { title: 'Intensive Outpatient', desc: 'Structured therapy multiple days per week (IOP)', icon: '📋', type: 'Intensive Outpatient' },
            ].map(item => (
              <Link key={item.type} to={`/search?type=${encodeURIComponent(item.type)}`}
                style={{ textDecoration: 'none', color: 'inherit' }}>
                <div style={{
                  padding: '24px', background: '#fff', borderRadius: '12px',
                  border: '1px solid #e0e0e0', boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                  cursor: 'pointer', transition: 'transform 0.2s',
                }}>
                  <div style={{ fontSize: '36px', marginBottom: '12px' }}>{item.icon}</div>
                  <h3 style={{ margin: '0 0 8px', fontSize: '18px', color: '#1a237e' }}>{item.title}</h3>
                  <p style={{ margin: 0, color: '#666', fontSize: '14px', lineHeight: 1.5 }}>{item.desc}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section style={{
        background: '#f5a623', padding: '60px 20px', textAlign: 'center'
      }}>
        <div style={{ maxWidth: '600px', margin: '0 auto' }}>
          <h2 style={{ fontSize: '32px', margin: '0 0 16px', color: '#fff' }}>Need Help Right Now?</h2>
          <p style={{ color: '#fff', opacity: 0.95, fontSize: '18px', marginBottom: '32px' }}>
            Our specialists are available 24/7 to help you find the right treatment program.
          </p>
          <a href="tel:+18006624357" onClick={() => trackAnalyticsEvent('helpline_click', { placement: 'home_cta' })} style={{
            display: 'inline-block', padding: '16px 40px', background: '#fff',
            color: '#f5a623', fontWeight: 800, fontSize: '20px',
            borderRadius: '8px', textDecoration: 'none',
          }}>
            📞 Call 1-800-662-4357
          </a>
          <p style={{ color: '#fff', opacity: 0.8, fontSize: '13px', marginTop: '12px' }}>
            Free, confidential, no obligation
          </p>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
